import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/nav/AppShell';
import { jobsApi, uploadApi, uploadUrl, issuesApi, pricingRulesApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { JobStatusPill } from '@/components/jobs/JobStatusPill';
import { JobStateTimeline } from '@/components/jobs/JobStateTimeline';
import { fmtDate, fmtMoney, NEXT_ACTION_LABEL, NEXT_STATUS, STATUS_LABELS } from '@/lib/format';
import { toast } from 'sonner';
import { JOBS } from '@/constants/testIds';
import { Trash2, Camera, Plus, AlertTriangle, X, ListChecks, Calculator, Ban, Phone, Home as HomeIcon, User } from 'lucide-react';
import { useAuth } from '@/lib/auth';

export default function JobDetailPage() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [openIssue, setOpenIssue] = useState(false);
  const [issueForm, setIssueForm] = useState({ title: '', description: '', severity: 'medium', photo_ids: [] });
  const [openAutoprice, setOpenAutoprice] = useState(false);
  const [rules, setRules] = useState([]);
  const [autoPriceForm, setAutoPriceForm] = useState({ rule_id: '', flags: {} });
  const [lineItems, setLineItems] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    const j = await jobsApi.get(id);
    setJob(j);
    setLineItems(j.line_items || []);
  };
  useEffect(() => { load(); pricingRulesApi.list().then(setRules); }, [id]);

  const advance = async () => {
    const next = NEXT_STATUS[job.status];
    if (!next) return;
    try {
      await jobsApi.transition(id, next);
      toast.success(`Job → ${STATUS_LABELS[next]}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transition failed');
    }
  };

  const cancel = async () => {
    if (!confirm('Cancel this job?')) return;
    try {
      await jobsApi.transition(id, 'cancelled');
      toast.success('Job cancelled');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const initChecklist = async () => {
    try {
      await jobsApi.initChecklist(id);
      toast.success('Checklist initialized');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const handlePhotoUpload = async (itemId, file) => {
    try {
      const up = await uploadApi.upload(file);
      await jobsApi.completeChecklistItem(id, itemId, { photo_id: up.id, completed: true });
      toast.success('Photo uploaded');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
  };

  const toggleChecklistItem = async (itemId, checked, photoRequired, existingPhoto) => {
    if (checked && photoRequired && !existingPhoto) {
      toast.error('Photo is required to complete this task.');
      return;
    }
    await jobsApi.completeChecklistItem(id, itemId, { photo_id: existingPhoto, completed: checked });
    load();
  };

  const submitIssue = async (e) => {
    e.preventDefault();
    try {
      await issuesApi.create({ ...issueForm, job_id: id });
      toast.success('Issue reported');
      setOpenIssue(false);
      setIssueForm({ title: '', description: '', severity: 'medium', photo_ids: [] });
      load();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const uploadIssuePhoto = async (file) => {
    try {
      const up = await uploadApi.upload(file);
      setIssueForm({ ...issueForm, photo_ids: [...issueForm.photo_ids, up.id] });
      toast.success('Photo attached');
    } catch {
      toast.error('Upload failed');
    }
  };

  const computeAutoPrice = async (e) => {
    e.preventDefault();
    try {
      const r = await pricingRulesApi.compute({
        rule_id: autoPriceForm.rule_id || null,
        property_id: job.property_id,
        flags: autoPriceForm.flags,
      });
      const newItems = r.line_items;
      setLineItems(newItems);
      await jobsApi.update(id, { customer_id: job.customer_id, property_id: job.property_id, line_items: newItems, assigned_cleaner_id: job.assigned_cleaner_id, scheduled_at: job.scheduled_at, duration_hours: job.duration_hours, notes: job.notes, source: job.source });
      toast.success(`Auto-priced: ${fmtMoney(r.total)}`);
      setOpenAutoprice(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const addLineItem = () => setLineItems([...lineItems, { label: '', qty: 1, unit_price: 0, taxable: true }]);
  const updateLineItem = (i, field, val) => {
    const arr = lineItems.map((li, idx) => idx === i ? { ...li, [field]: val } : li);
    setLineItems(arr);
  };
  const removeLineItem = (i) => setLineItems(lineItems.filter((_, idx) => idx !== i));
  const saveLineItems = async () => {
    try {
      await jobsApi.update(id, { customer_id: job.customer_id, property_id: job.property_id, line_items: lineItems, assigned_cleaner_id: job.assigned_cleaner_id, scheduled_at: job.scheduled_at, duration_hours: job.duration_hours, notes: job.notes, source: job.source });
      toast.success('Line items saved');
      load();
    } catch (e) {
      toast.error('Failed');
    }
  };

  if (!job) return <AppShell title="Job"><div className="text-sm text-muted-foreground">Loading…</div></AppShell>;

  const canAdvance = !!NEXT_STATUS[job.status];
  const subtotal = (lineItems || []).reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_price) || 0), 0);
  const defaultRule = rules.find(r => r.is_default) || rules[0];

  return (
    <AppShell title={`Job ${id.slice(0, 6)}`}>
      <div className="space-y-6 pb-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl md:text-3xl font-semibold">Job {job.id.slice(0, 6)}</h1>
              <JobStatusPill status={job.status} />
              {job.source === 'ical' && <Badge variant="outline" className="text-[10px]">iCal turnover</Badge>}
            </div>
            <div className="mt-2 text-sm text-muted-foreground flex flex-wrap items-center gap-3">
              {job.customer && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /><Link to={`/customers/${job.customer.id}`} className="hover:underline">{job.customer.name}</Link></span>}
              {job.property && <span className="flex items-center gap-1"><HomeIcon className="h-3.5 w-3.5" /><Link to={`/properties/${job.property.id}`} className="hover:underline">{job.property.nickname}</Link></span>}
              <span>🗓️ {fmtDate(job.scheduled_at)}</span>
              {job.duration_hours && <span>{job.duration_hours}h</span>}
              {job.cleaner && <span>🧹 {job.cleaner.name}</span>}
            </div>
          </div>
        </div>

        {/* State timeline */}
        <Card>
          <CardContent className="p-4"><JobStateTimeline status={job.status} history={job.history} /></CardContent>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <div className="xl:col-span-8 space-y-4">
            {/* Line items */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Line items</CardTitle>
                <div className="flex gap-2">
                  {user?.role === 'admin' && (
                    <Dialog open={openAutoprice} onOpenChange={setOpenAutoprice}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid={JOBS.detailAutoPrice}><Calculator className="h-3 w-3 mr-1" />Auto-price</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Auto-price this job</DialogTitle></DialogHeader>
                        <form onSubmit={computeAutoPrice} className="space-y-3">
                          <div>
                            <Label>Pricing rule</Label>
                            <select className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong" value={autoPriceForm.rule_id || ''} onChange={e => setAutoPriceForm({ ...autoPriceForm, rule_id: e.target.value })}>
                              <option value="">Default: {defaultRule?.name || 'None'}</option>
                              {rules.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                          </div>
                          <div className="text-xs text-muted-foreground">Uses property attributes: {job.property?.bedrooms || 0} bd · {job.property?.bathrooms || 0} ba · {job.property?.sqft || 0} sqft</div>
                          <div>
                            <Label>Extras</Label>
                            <div className="space-y-1 mt-1">
                              {(defaultRule?.extras || []).map(ex => (
                                <label key={ex.key} className="flex items-center gap-2 text-sm">
                                  <Checkbox checked={!!autoPriceForm.flags[ex.key]} onCheckedChange={v => setAutoPriceForm({ ...autoPriceForm, flags: { ...autoPriceForm.flags, [ex.key]: !!v } })} />
                                  {ex.label} <span className="text-muted-foreground">(+{fmtMoney(ex.price)})</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          <DialogFooter><Button type="submit">Compute & apply</Button></DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                  {user?.role === 'admin' && (
                    <Button size="sm" variant="outline" onClick={addLineItem} data-testid={JOBS.addLineItem}><Plus className="h-3 w-3 mr-1" />Add item</Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {lineItems.length === 0 && <div className="text-sm text-muted-foreground">No line items. Use Auto-price or add manually.</div>}
                {lineItems.map((li, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center">
                    <Input className="col-span-2 sm:col-span-5" placeholder="Description" value={li.label} onChange={e => updateLineItem(i, 'label', e.target.value)} />
                    <Input className="col-span-1 sm:col-span-2" type="number" step="0.01" value={li.qty} onChange={e => updateLineItem(i, 'qty', parseFloat(e.target.value) || 0)} />
                    <Input className="col-span-1 sm:col-span-3" type="number" step="0.01" value={li.unit_price} onChange={e => updateLineItem(i, 'unit_price', parseFloat(e.target.value) || 0)} />
                    <div className="col-span-1 text-right tabular-nums text-sm font-medium">{fmtMoney((li.qty || 0) * (li.unit_price || 0))}</div>
                    <Button size="icon" variant="ghost" className="col-span-1 justify-self-end sm:justify-self-auto" onClick={() => removeLineItem(i)}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
                {lineItems.length > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-sm text-muted-foreground">Subtotal</div>
                    <div className="font-semibold tabular-nums">{fmtMoney(subtotal)}</div>
                  </div>
                )}
                {user?.role === 'admin' && lineItems.length > 0 && (
                  <div className="flex justify-end"><Button size="sm" onClick={saveLineItems}>Save line items</Button></div>
                )}
              </CardContent>
            </Card>

            {/* Checklist */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><ListChecks className="h-4 w-4" />Checklist</CardTitle>
                {!job.checklist && <Button size="sm" onClick={initChecklist} data-testid={JOBS.checklistInit}>Start checklist</Button>}
              </CardHeader>
              <CardContent>
                {!job.checklist ? (
                  <div className="text-sm text-muted-foreground">No checklist yet. Click "Start checklist" to instantiate one from the property template.</div>
                ) : job.checklist.items.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Checklist template has no items. Add items on the property page.</div>
                ) : (
                  <div className="space-y-2">
                    {job.checklist.items.map(it => (
                      <div key={it.id} className={`border rounded-lg p-3 ${it.completed ? 'bg-muted/40' : 'bg-card'}`}>
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={!!it.completed}
                            data-testid={JOBS.checklistItemComplete}
                            onCheckedChange={v => toggleChecklistItem(it.id, !!v, it.photo_required, it.photo_id)}
                          />
                          <div className="flex-1">
                            <div className={`text-sm font-medium ${it.completed ? 'line-through text-muted-foreground' : ''}`}>{it.title}</div>
                            {it.description && <div className="text-xs text-muted-foreground mt-0.5">{it.description}</div>}
                            {it.photo_required && <Badge variant="outline" className="mt-1 text-[10px]"><Camera className="h-3 w-3 mr-1" />Photo required</Badge>}
                            {it.photo_id && (
                              <div className="mt-2">
                                <img src={uploadUrl(`/uploads/${it.photo_id}`) || '#'} alt="proof" className="h-24 w-24 rounded-md object-cover border" onError={(e) => { e.target.style.display = 'none'; }} />
                                <PhotoResolve photoId={it.photo_id} />
                              </div>
                            )}
                          </div>
                          <label className="cursor-pointer">
                            <input type="file" accept="image/*" capture="environment" className="hidden" data-testid={JOBS.checklistItemUpload}
                              onChange={e => e.target.files?.[0] && handlePhotoUpload(it.id, e.target.files[0])} />
                            <Button size="sm" variant="outline" type="button" asChild data-testid={JOBS.checklistItemCapture}>
                              <span><Camera className="h-3 w-3 mr-1" />{it.photo_id ? 'Retake' : 'Photo'}</span>
                            </Button>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Issues */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Issues reported ({(job.issues || []).length})</CardTitle>
                <Dialog open={openIssue} onOpenChange={setOpenIssue}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid={JOBS.reportIssue}><Plus className="h-3 w-3 mr-1" />Report</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Report an issue</DialogTitle></DialogHeader>
                    <form onSubmit={submitIssue} className="space-y-3">
                      <div><Label>Title *</Label><Input required value={issueForm.title} onChange={e => setIssueForm({ ...issueForm, title: e.target.value })} placeholder="Broken lamp in bedroom" /></div>
                      <div><Label>Description</Label><Textarea value={issueForm.description} onChange={e => setIssueForm({ ...issueForm, description: e.target.value })} /></div>
                      <div>
                        <Label>Severity</Label>
                        <select className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong" value={issueForm.severity} onChange={e => setIssueForm({ ...issueForm, severity: e.target.value })}>
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      <div>
                        <Label>Photos</Label>
                        <input type="file" accept="image/*" onChange={e => e.target.files?.[0] && uploadIssuePhoto(e.target.files[0])} className="text-sm" />
                        {issueForm.photo_ids.length > 0 && <div className="text-xs text-muted-foreground mt-1">{issueForm.photo_ids.length} photo(s) attached</div>}
                      </div>
                      <DialogFooter><Button type="submit">Submit</Button></DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="space-y-2">
                {(job.issues || []).length === 0 && <div className="text-sm text-muted-foreground">No issues reported.</div>}
                {(job.issues || []).map(iss => (
                  <div key={iss.id} className="border rounded-lg p-3 bg-card">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm">{iss.title}</div>
                      <Badge variant="outline" className="text-[10px]">{iss.status} · {iss.severity}</Badge>
                    </div>
                    {iss.description && <div className="text-xs text-muted-foreground mt-1">{iss.description}</div>}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="xl:col-span-4 space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Scheduled</span><span>{fmtDate(job.scheduled_at)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Duration</span><span>{job.duration_hours || 3}h</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Cleaner</span><span>{job.cleaner?.name || 'Unassigned'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Source</span><span className="capitalize">{job.source || 'manual'}</span></div>
                {job.notes && <div className="pt-2 border-t text-xs">{job.notes}</div>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">History</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(job.history || []).map((h, i) => (
                    <div key={i} className="text-xs">
                      <span className="text-muted-foreground">{fmtDate(h.at)}</span> • <span className="font-medium">{h.from || 'created'}</span> → <span className="font-semibold">{h.to}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-[264px] z-30 border-t bg-card shadow-[var(--shadow-md)] px-4 md:px-6 lg:px-8 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between gap-3 max-w-[1440px] mx-auto">
          {/* Hidden on phones: the status is already on the page, and keeping it
              here pushed the primary action off the right edge. */}
          <div className="hidden sm:flex items-center gap-2 min-w-0">
            <JobStatusPill status={job.status} />
            <div className="text-xs text-muted-foreground truncate">Job {job.id.slice(0, 6)}</div>
          </div>
          <div className="flex flex-1 sm:flex-none items-center gap-2">
            {job.status !== 'cancelled' && !['paid', 'refunded'].includes(job.status) && (
              <Button variant="outline" size="sm" onClick={cancel} data-testid={JOBS.detailCancel}><Ban className="h-4 w-4 mr-1" />Cancel</Button>
            )}
            {canAdvance && (
              <Button onClick={advance} data-testid={JOBS.detailAdvance} className="flex-1 sm:flex-none sm:min-w-[160px]">
                {NEXT_ACTION_LABEL[job.status]}
              </Button>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function PhotoResolve({ photoId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { api } = await import('@/lib/api');
        const r = await api.get(`/uploads/${photoId}/info`);
        setUrl(r.data.url);
      } catch {}
    })();
  }, [photoId]);
  if (!url) return null;
  return (
    <img src={uploadUrl(url)} alt="proof" className="h-24 w-24 rounded-md object-cover border mt-2" />
  );
}
